import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaArrowRight, FaEnvelope, FaImages } from "react-icons/fa";
import logoForHeroSection1 from "../../assets/images/logoForHeroSection1.svg";
import { ROUTES } from "../../constants/routes";
import { scrollToTop } from "../../utils/scroll";
import { isRtlLanguage } from "../../utils/i18n";
import HeroSocialLinks from "./HeroSocialLinks";
import "./HeroSection.css";

function HeroSection({
  backgroundImage,
  blendClass = "style-blend-mode",
  titleKey,
  subtitleKey,
  variant = "slide",
  subtitleSize = "lg",
  contentHeightClass = "myComponent min-h-[88vh] sm:min-h-screen",
}) {
  const { t, i18n } = useTranslation();
  const isRtl = isRtlLanguage(i18n.language);
  const isPage = variant === "page";
  const heightClass = isPage ? "hero-page-height" : contentHeightClass;

  const textAlign = isPage ? "text-center" : "text-center md:text-start";
  const panelAlign = isPage
    ? "hero-text-panel hero-text-panel--page"
    : `hero-text-panel w-full rounded-2xl p-5 sm:p-7 md:p-8 lg:p-9 ${textAlign}`;

  const titleClass = isPage
    ? "hero-copy hero-page-title"
    : `hero-copy text-[1.85rem] font-bold leading-tight tracking-tight text-white sm:text-4xl md:text-[2.65rem] lg:text-5xl ${textAlign}`;

  const subtitleClass = isPage
    ? "hero-copy hero-page-subtitle mx-auto"
    : `hero-copy hero-subtitle hero-subtitle--${subtitleSize} ${textAlign} md:me-auto`;

  const accentClass = isPage
    ? "hero-title-accent hero-title-accent--page"
    : "hero-title-accent hero-title-accent--slide";

  const rowDirection = isRtl ? "md:flex-row-reverse" : "md:flex-row";
  const ArrowIcon = isRtl ? FaArrowLeft : FaArrowRight;

  return (
    <div className={`hero-slide relative${isPage ? " hero-slide--page" : ""}`}>
      <div
        className="hero-bg-image absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${backgroundImage})` }}
        aria-hidden="true"
      />
      <div className={`absolute inset-0 ${blendClass}`} aria-hidden="true" />
      <div
        className={
          isPage
            ? "hero-page-gradient absolute inset-0"
            : "hero-gradient absolute inset-0"
        }
        aria-hidden="true"
      />
      <div className="hero-vignette absolute inset-0" aria-hidden="true" />

      <div
        className={`hero-inner relative z-10 mx-auto flex w-full max-w-6xl items-center px-4 py-12 sm:px-6 sm:py-14 lg:max-w-7xl lg:px-8 ${heightClass}${isPage ? " hero-inner--page" : ""}`}
        dir={isRtl ? "rtl" : "ltr"}
      >
        <div
          className={`hero-content-inner flex w-full flex-col items-center gap-5 ${rowDirection} md:items-stretch md:gap-6 lg:gap-8`}
        >
          {!isPage && (
            <div className="hero-logo-wrap md:w-[34%] lg:w-[32%]">
              <div className="hero-logo-ring">
                <img
                  src={logoForHeroSection1}
                  alt="CAF Sports"
                  className="hero-logo mx-auto w-[8.5rem] sm:w-[10rem] md:w-full md:max-w-[11rem] lg:max-w-[13rem]"
                />
              </div>
            </div>
          )}

          <div className={`flex flex-1 flex-col ${isPage ? "w-full" : ""}`}>
            <div className={panelAlign}>
              {isPage ? (
                <span className="hero-page-eyebrow">
                  {t("CAF")} {t("SPORTS")}
                </span>
              ) : (
                <span className="hero-eyebrow">
                  {t("CAF")} {t("SPORTS")}
                </span>
              )}
              <div className={accentClass} aria-hidden="true" />
              <h1 className={titleClass}>{t(titleKey)}</h1>
              {subtitleKey && <p className={subtitleClass}>{t(subtitleKey)}</p>}

              {!isPage && (
                <div className="hero-cta-row">
                  <Link
                    to={ROUTES.projects}
                    onClick={scrollToTop}
                    className="hero-btn hero-btn--primary"
                  >
                    <span
                      className="hero-btn__icon-wrap hero-btn__icon-wrap--lead"
                      aria-hidden="true"
                    >
                      <FaImages className="hero-btn__icon" />
                    </span>
                    <span className="hero-btn__label">{t("Our Projects")}</span>
                    <span className="hero-btn__icon-wrap" aria-hidden="true">
                      <ArrowIcon className="hero-btn__icon" />
                    </span>
                  </Link>
                  <Link
                    to={ROUTES.contact}
                    onClick={scrollToTop}
                    className="hero-btn hero-btn--secondary"
                  >
                    <span
                      className="hero-btn__icon-wrap hero-btn__icon-wrap--muted"
                      aria-hidden="true"
                    >
                      <FaEnvelope className="hero-btn__icon" />
                    </span>
                    <span className="hero-btn__label">{t("Contact Us")}</span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {!isPage && <HeroSocialLinks isRtl={isRtl} />}
    </div>
  );
}

export default HeroSection;
